import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { de } from 'date-fns/locale';

/** Merge Tailwind classes with conflict resolution */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format date relative ("vor 5 Min.", "Gestern", "12. Feb.") */
export function formatRelativeDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isToday(d)) {
    return formatDistanceToNow(d, { addSuffix: true, locale: de });
  }
  if (isYesterday(d)) {
    return `Gestern, ${format(d, 'HH:mm')}`;
  }
  return format(d, 'd. MMM. HH:mm', { locale: de });
}

/** Format time "08:30" */
export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'HH:mm');
}

/** Format date "12. Feb. 2026" */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'd. MMM yyyy', { locale: de });
}

/** Format call duration "2:34" or "0:12" */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Greeting based on time of day */
export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

/** Truncate text with ellipsis */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '…';
}

/** Check if DEV_DEMO_MODE is active */
export function isDevDemoMode(): boolean {
  // Hard gate: demo mode must never be active in production builds.
  return import.meta.env.DEV && import.meta.env.VITE_DEV_DEMO_MODE === 'true';
}
